import * as THREE from "three";
import { defineModel, type Part, type Variant } from "../../lib";

// A blasted tree of no-man's-land: a bare, dark trunk snapped off with a
// jagged crown of splinters and a stub branch or two — no leaves, nothing
// alive. Two variants for scattering: "tall" (snapped at about 1.2 units)
// and "stump" (blown down to knee height).
const DEAD = "#4f4238";
const DEAD_DARK = "#443a30";
const MUD = "#5d4a33";

// The jagged break: thin angled boxes fanned around the top of the trunk,
// offset from centre so the silhouette reads as torn wood, not a flat cut.
function splinters(topY: number, r: number, hScale = 1): Part[] {
  const spec: Array<[number, number, number, number]> = [
    // [angle around trunk, splinter height, outward lean, extra rise]
    [0.4, 0.3, 0.28, 0.1],
    [1.9, 0.22, -0.22, 0.0],
    [3.5, 0.36, 0.18, 0.14],
    [5.1, 0.18, -0.3, -0.02],
  ];
  return spec.map(([a, h, lean, rise]) => ({
    geometry: new THREE.BoxGeometry(0.055, h * hScale, 0.05),
    color: DEAD_DARK,
    position: [Math.cos(a) * r * 0.6, topY + (h * hScale) / 2 + rise * hScale, Math.sin(a) * r * 0.6],
    rotation: [Math.sin(a) * lean, a, Math.cos(a) * lean],
  }));
}

function base(): Part[] {
  return [
    // Root flare and a heap of mud at the foot
    { geometry: new THREE.CylinderGeometry(0.12, 0.19, 0.12, 8), color: DEAD, position: [0, 0.06, 0] },
    { geometry: new THREE.IcosahedronGeometry(0.09, 0), color: MUD,
      position: [0.16, 0.04, 0.1], scale: [1.2, 0.6, 1.0] },
  ];
}

const variants: Variant[] = [
  {
    id: "tall",
    label: "Tall",
    description: "Snapped at head height, two bare stub branches remaining.",
    parts: () => [
      ...base(),
      { geometry: new THREE.CylinderGeometry(0.075, 0.12, 1.2, 8), color: DEAD, position: [0, 0.6, 0], rotation: [0, 0, 0.04] },
      ...splinters(1.18, 0.075),
      // Two bare stub branches, snapped short
      { geometry: new THREE.CylinderGeometry(0.02, 0.035, 0.34, 6), color: DEAD_DARK,
        position: [0.16, 0.86, 0.04], rotation: [0.15, 0, -1.05] },
      { geometry: new THREE.CylinderGeometry(0.018, 0.03, 0.26, 6), color: DEAD_DARK,
        position: [-0.12, 0.62, -0.08], rotation: [-0.3, 0, 1.15] },
    ],
  },
  {
    id: "stump",
    label: "Stump",
    description: "Blown down to a splintered knee-high stump.",
    parts: () => [
      ...base(),
      { geometry: new THREE.CylinderGeometry(0.09, 0.13, 0.42, 8), color: DEAD, position: [0, 0.21, 0], rotation: [0, 0.6, -0.03] },
      ...splinters(0.4, 0.09, 0.55),
      // A shard of the fallen trunk lying beside the stump
      { geometry: new THREE.CylinderGeometry(0.05, 0.07, 0.5, 7), color: DEAD_DARK,
        position: [0.24, 0.06, -0.14], rotation: [Math.PI / 2 - 0.12, 0.5, 0] },
    ],
  },
];

export const shatteredTree = defineModel({
  id: "shattered-tree",
  batchId: "wwi",
  author: "Fable 5",
  name: "Shattered Tree",
  tags: ["wwi", "scenery", "nature", "tree", "battlefield"],
  summary: "A blasted, leafless tree snapped to a jagged splintered break; tall and stump variants.",
  footprint: 0.3,
  tiles: { w: 1, h: 1 },
  variants,
  animations: [],
});
